import React, { useContext } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Typography } from "@mui/material";

import styles from "./ChatBubble.module.css";
import phoneStyles from "./PhoneBubble.module.css";

import intim1 from "../assets/intim1.png";
import dontClickVideo from "../assets/dontClickVideo.png";
import { StyledButton } from "../components/";
import { LangContext } from "../contexts/langContext";
import { getButtonPalletFromCorrectOrIncorrect } from "../util/getButtonPalletFromCorrectOrIncorrect";
import useShowingCorrectAnswer from "../hooks/useShowingCorrectAnswer";
import { SelectAnswerContext } from "../contexts/selectAnswerContext";
import { ChatQuestionRenderProps, TranslationData } from "@common";

interface RenderProps extends ChatQuestionRenderProps {
  readOnly?: boolean;
  showCorrect?: boolean;
}

const ChatQuestion: React.FC<{
  [key in keyof RenderProps]: RenderProps[key];
}> = ({
  yourAnswer,
  from,
  type,
  text1SideText,
  text1Side,
  text2SideText,
  text2Side,
  text3SideText,
  text3Side,
  text4SideText,
  text4Side,
  answerOption1Text,
  answerOption1IsCorrect,
  answerOption2Text,
  answerOption2IsCorrect,
  readOnly = false,
  showCorrect = false,
}) => {
  const { translateTranslationData } = useContext(LangContext);
  const { onSelectAnswer } = useContext(SelectAnswerContext);
  const showingCorrectAnswer = useShowingCorrectAnswer();
  const displayCorrectAnswers = showCorrect || showingCorrectAnswer;

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        fontFamily: "arial",
      }}
    >
      {type === "sms" && (
        <SMSContainer
          type={type}
          yourAnswer={yourAnswer}
          from={from}
          text1SideText={text1SideText}
          text1Side={text1Side}
          text2SideText={text2SideText}
          text2Side={text2Side}
          text3SideText={text3SideText}
          text3Side={text3Side}
          text4SideText={text4SideText}
          text4Side={text4Side}
        />
      )}

      {type === "phone" && (
        <PhoneContainer
          yourAnswer={yourAnswer}
          from={from}
          text1SideText={text1SideText}
          text1Side={text1Side}
          text2SideText={text2SideText}
          text2Side={text2Side}
          text3SideText={text3SideText}
          text3Side={text3Side}
          text4SideText={text4SideText}
          text4Side={text4Side}
        />
      )}

      <div style={{ margin: "6px 0", display: "flex", flexDirection: "column" }}>
        <div>
          <StyledButton
            hasCooldown
            disabled={readOnly}
            onClick={() =>
              onSelectAnswer(answerOption1IsCorrect, {
                val1: 0,
                val2: answerOption1IsCorrect === "correct" ? 1 : 0,
                val3: answerOption1IsCorrect === "incorrect" ? 1 : 0,
              })
            }
            label={translateTranslationData(answerOption1Text)}
            style={{
              margin: "5px 2px",
              textAlign: "left",
            }}
            clickable={displayCorrectAnswers === false}
            palette={getButtonPalletFromCorrectOrIncorrect(
              displayCorrectAnswers,
              answerOption1IsCorrect
            )}
          />
          <StyledButton
            hasCooldown
            disabled={readOnly}
            onClick={() =>
              onSelectAnswer(answerOption2IsCorrect, {
                val1: 0,
                val2: answerOption2IsCorrect === "correct" ? 1 : 0,
                val3: answerOption2IsCorrect === "incorrect" ? 1 : 0,
              })
            }
            label={translateTranslationData(answerOption2Text)}
            style={{
              margin: "5px 2px",
              textAlign: "left",
            }}
            clickable={displayCorrectAnswers === false}
            palette={getButtonPalletFromCorrectOrIncorrect(
              displayCorrectAnswers,
              answerOption2IsCorrect
            )}
          />
        </div>
      </div>
    </div>
  );
};

const SMSContainer: React.FC<{
  type: "sms" | "phone";
  yourAnswer: TranslationData;
  from: TranslationData;
  text1SideText: TranslationData;
  text1Side: "left" | "right";
  text2SideText: TranslationData;
  text2Side: "left" | "right";
  text3SideText: TranslationData;
  text3Side: "left" | "right";
  text4SideText: TranslationData;
  text4Side: "left" | "right";
}> = ({
  type,
  yourAnswer,
  from,
  text1SideText,
  text1Side,
  text2SideText,
  text2Side,
  text3SideText,
  text3Side,
  text4SideText,
  text4Side,
}) => {
  const { translateTranslationData } = useContext(LangContext);

  return (
    <div
      style={{
        position: "relative",
        background: type === "sms" ? "white" : undefined,
        paddingBottom: 42,
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "100%",
          textAlign: "center",
          background: "rgba(0,0,0,0.1)",
        }}
      >
        <p style={{ padding: "6px 0", margin: 0, color: "grey" }}>
          {translateTranslationData(from)}
        </p>
      </div>

      <div
        style={{
          margin: "4px 6px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SMS side={text1Side} text={translateTranslationData(text1SideText)} />
        <SMS side={text2Side} text={translateTranslationData(text2SideText)} />
        <SMS side={text3Side} text={translateTranslationData(text3SideText)} />
        <SMS side={text4Side} text={translateTranslationData(text4SideText)} />
      </div>

      <div
        style={{
          position: "absolute",
          width: "100%",
          bottom: 0,
          background: "rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            flex: 1,
            background: "white",
            borderRadius: 20,
            margin: 6,
          }}
        >
          <Typography
            variant="body2"
            style={{
              paddingLeft: 12,
              opacity: 0.9,
              margin: 0,
              color: "grey",
            }}
          >
            {translateTranslationData(yourAnswer)}
          </Typography>
        </div>
      </div>
    </div>
  );
};

const SMS: React.FC<{ side: "left" | "right"; text: string }> = ({ side, text }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  let imgSrc = "";
  if (text === "image:intim1") {
    imgSrc = intim1;
  } else if (text === "image:dontClickVideo") {
    imgSrc = dontClickVideo;
  }

  if (imgSrc === "") {
    if (text === "") return null;

    return (
      <div
        style={{ margin: "4px" }}
        className={
          side === "left"
            ? `${styles.yours} ${styles.messages}`
            : `${styles.mine} ${styles.messages}`
        }
      >
        <div className={`${styles.message} ${styles.last}`}>
          <Typography variant="body2">{text}</Typography>
        </div>
      </div>
    );
  } else {
    return (
      <img
        src={imgSrc}
        alt=""
        style={{
          height: isMobile ? 60 : "100%",
          margin: "4px 0",
          alignSelf: side === "left" ? "flex-start" : "flex-end",
          borderRadius: 20,
        }}
      />
    );
  }
};

const PhoneContainer: React.FC<{
  yourAnswer: TranslationData;
  from: TranslationData;
  text1SideText: TranslationData;
  text1Side: "left" | "right";
  text2SideText: TranslationData;
  text2Side: "left" | "right";
  text3SideText: TranslationData;
  text3Side: "left" | "right";
  text4SideText: TranslationData;
  text4Side: "left" | "right";
}> = ({
  yourAnswer,
  from,
  text1SideText,
  text1Side,
  text2SideText,
  text2Side,
  text3SideText,
  text3Side,
  text4SideText,
  text4Side,
}) => {
  const { translateTranslationData } = useContext(LangContext);

  return (
    <div
      style={{
        margin: "0",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Phone
        from={translateTranslationData(from)}
        side={text1Side}
        text={translateTranslationData(text1SideText)}
      />
      <Phone
        from={translateTranslationData(from)}
        side={text2Side}
        text={translateTranslationData(text2SideText)}
      />
      <Phone
        from={translateTranslationData(from)}
        side={text3Side}
        text={translateTranslationData(text3SideText)}
      />
      <Phone
        from={translateTranslationData(from)}
        side={text4Side}
        text={translateTranslationData(text4SideText)}
      />
      <Typography mt={4} variant="h5" fontWeight={500}>
        {translateTranslationData(yourAnswer)}
      </Typography>
    </div>
  );
};

const Phone: React.FC<{ side: "left" | "right"; text: string; from: string }> = ({
  side,
  text,
  from,
}) => {
  if (text === "") return null;
  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        justifyContent: side === "right" ? "flex-end" : "flex-start",
      }}
    >
      <div
        style={{ margin: "4px", position: "relative" }}
        className={
          side === "left"
            ? `${phoneStyles.yours} ${phoneStyles.messages}`
            : `${phoneStyles.mine} ${phoneStyles.messages}`
        }
      >
        <div
          className={`${phoneStyles.message} ${phoneStyles.last}`}
          style={{ alignSelf: "flex-start" }}
        >
          <Typography>
            <EmojiResponse text={from} side={side} /> {text}
          </Typography>
        </div>
      </div>
    </div>
  );
};

const EmojiResponse: React.FC<{ text: string; side: "left" | "right" }> = ({ text, side }) => {
  const emoji = side === "right" ? "📱" : "📞";
  const from = side === "right" ? "Du" : text;
  return (
    <span style={{ color: "grey" }}>
      <span style={{ marginRight: 6 }}>{emoji}</span>
      {from}:
    </span>
  );
};

export default ChatQuestion;
